/**
 * Rock typing — jenis batuan reservoir dari porositas dan permeabilitas.
 *
 * Satu layar untuk satu pertanyaan: *berapa jenis batuan yang ada di sini, dan
 * bagaimana masing-masing menghubungkan φ dengan k*. Sebuah plot poroperm yang
 * tidak diwarnai tidak bisa menjawabnya — awan lebar yang sebenarnya tiga tren
 * bertumpuk terlihat persis seperti awan lebar yang memang satu — dan sebuah
 * regresi tunggal atas seluruhnya memulangkan persamaan yang tidak berlaku di
 * satu pun trennya.
 *
 * Tiga hal yang dipisahkan dengan sengaja:
 *
 * **Memisahkan dan menaksir adalah dua pekerjaan.** Kiri memisahkan — ambang
 * atau pengelompokan, keduanya dari φ dan k sendiri. Panel ketiga menaksir:
 * sebuah penggolong yang belajar membaca jenis batuan dari kurva log, supaya
 * sumur yang **tidak punya permeabilitas** tetap bisa dikelaskan. Yang kedua
 * tidak pernah menimpa yang pertama; hasilnya berdiri sebagai kurva kedua.
 *
 * **Hitungan berjalan ketika diminta, bukan pada tiap sentuhan penggeser.**
 * Mengelompokkan seratus ribu sampel berbiaya detik, dan sebuah layar yang
 * menghitung ulang pada tiap ketikan menghabiskannya untuk hasil yang tidak
 * pernah dilihat siapa pun.
 *
 * **Yang membantah ikut dipajang.** Sapuan siluet berdiri di sebelah jumlah
 * kelas yang dipilih, dan angka buta berdiri di sebelah angka latih. Sebuah
 * layar yang hanya memajang yang mendukung adalah layar yang meyakinkan.
 */

import { useMemo, useState } from "react";
import { api } from "../api";
import { canCompute } from "../auth";
import {
  Button,
  Checkbox,
  Dropdown,
  Empty,
  KeyValue,
  Loading,
  Notice,
  NumberField,
  OptionalNumberField,
  Panel,
  Segmented,
  useToast,
} from "./ui";
import { useAsync } from "../hooks";
import {
  classColor,
  decadeBounds,
  decadeTicks,
  equationOf,
  fitLine,
  linearPlace,
  logPlace,
  num,
  occupied,
  permText,
  sweepVerdict,
} from "../lib/rocktype";
import { distinctColors } from "../lib/xplot";
import type {
  Project,
  RockTypeBoundary,
  RockTypeCatalogue,
  RockTyping as RockTypingPayload,
  Well,
} from "../types";

/** Bingkai gambar, sama dengan yang dipakai crossplot supaya keduanya sepadan. */
const PAD = 3;
const SPAN = 100 - PAD * 2;

/** Ejaan porositas dan permeabilitas yang lazim, dalam urutan pilihan pertama. */
const PHI_NAMES = ["PHIE", "PHIT", "PHI", "PHIA", "CPOR"];
const PERM_NAMES = ["PERM", "K", "KINT", "CKH", "KH"];

const pick = (available: string[], wanted: string[]): string =>
  wanted.find((name) => available.includes(name)) ?? available[0] ?? "";

type Tab = "classes" | "sweep" | "ml" | "wells";

export default function RockTyping({
  project,
  wells,
}: {
  project: Project;
  wells: Well[];
}) {
  const toast = useToast();
  const catalogue = useAsync(
    (signal) =>
      api.get<RockTypeCatalogue>(`/api/projects/${project.id}/rock-typing/`, undefined, signal),
    [project.id],
  );

  const available = catalogue.data?.available ?? [];
  const [phiPick, setPhiPick] = useState("");
  const [permPick, setPermPick] = useState("");
  const phi = phiPick || pick(available, PHI_NAMES);
  const perm = permPick || pick(available, PERM_NAMES);

  const [selection, setSelection] = useState<Set<number> | null>(null);
  const chosen = selection ?? new Set(wells.map((w) => w.id));

  const [method, setMethod] = useState("fzi");
  const [classes, setClasses] = useState(4);
  const [space, setSpace] = useState("fzi");
  const [cutoffs, setCutoffs] = useState("4, 2, 1");
  const [zone, setZone] = useState("");
  const [vshMax, setVshMax] = useState<number | null>(null);
  const [swMin, setSwMin] = useState<number | null>(null);

  const [mlOn, setMlOn] = useState(false);
  const [mlKind, setMlKind] = useState("random_forest");
  const [mlInputs, setMlInputs] = useState<string[] | null>(null);
  /**
   * Kurva yang benar-benar dikirim sebagai masukan penggolong.
   *
   * Porositas dan permeabilitasnya sendiri disaring di sini dan bukan hanya
   * disembunyikan dari daftar centang: sebuah pilihan yang dibuat sebelum
   * kurva sumbunya diganti akan tetap terbawa, tidak terlihat di layar, dan
   * ditolak server dengan kalimat tentang sesuatu yang tidak pernah dicentang
   * siapa pun.
   */
  const inputs = (mlInputs ?? catalogue.data?.suggested_inputs ?? []).filter(
    (name) => available.includes(name) && name !== phi && name !== perm,
  );

  const [colourBy, setColourBy] = useState<"class" | "well">("class");
  const [tab, setTab] = useState<Tab>("classes");
  const [result, setResult] = useState<RockTypingPayload | null>(null);
  const [running, setRunning] = useState(false);

  const methods = catalogue.data?.methods ?? [];
  const chosenMethod = methods.find((m) => m.key === method) ?? null;
  const clustering = chosenMethod?.family === "cluster";
  const mayPublish = canCompute(project.my_role);

  /** Nama zona yang benar-benar dimiliki sumur terpilih, tanpa pengulangan. */
  const zoneNames = useMemo(() => {
    const names = new Set<string>();
    for (const well of wells) {
      if (!chosen.has(well.id)) continue;
      for (const z of well.zones ?? []) names.add(z.name);
    }
    return [...names].sort();
  }, [wells, chosen]);

  const body = () => ({
    wells: [...chosen],
    phi,
    perm,
    method,
    classes,
    features: space,
    cutoffs: method === "manual"
      ? cutoffs.split(/[,\s]+/).map(Number).filter((v) => Number.isFinite(v) && v > 0)
      : undefined,
    zones: zone ? [zone] : [],
    vsh_max: vshMax,
    sw_min: swMin,
    ml: mlOn ? { inputs, kind: mlKind } : null,
  });

  const run = async (publish: boolean) => {
    setRunning(true);
    try {
      const data = await api.post<RockTypingPayload>(
        `/api/projects/${project.id}/rock-typing/`,
        { ...body(), publish },
      );
      setResult(data);
      const named = occupied(data.classes).length;
      if (publish) {
        const written = (data.published?.wells ?? []).filter((row) => row.curve_set).length;
        toast(`Rock types published to ${written} wells as the ROCKTYPE curve set.`);
      } else {
        toast(`${named} rock types over ${data.points.count.toLocaleString("en-US")} samples.`);
      }
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setRunning(false);
    }
  };

  if (catalogue.loading) return <Loading label="Reading the curves…" />;
  if (catalogue.error) return <Empty>Rock typing could not be opened: {catalogue.error}</Empty>;

  return (
    <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
      <div
        className="scroll-y"
        style={{
          width: 272,
          flex: "none",
          padding: 10,
          display: "flex",
          flexDirection: "column",
          gap: 9,
          borderRight: "1px solid var(--line)",
        }}
      >
        <Panel title="Samples">
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            <Dropdown
              label="Porosity"
              value={phi}
              options={available.map((name) => ({ value: name, label: name }))}
              onChange={setPhiPick}
            />
            <Dropdown
              label="Permeability"
              value={perm}
              options={available.map((name) => ({ value: name, label: name }))}
              onChange={setPermPick}
            />
            <Dropdown
              label="Interval"
              value={zone}
              options={[
                { value: "", label: "Every depth" },
                ...zoneNames.map((name) => ({ value: name, label: name })),
              ]}
              onChange={setZone}
            />
            <OptionalNumberField label="VSH at most" value={vshMax} onChange={setVshMax}
                                 step={0.05} min={0} max={1} />
            <OptionalNumberField label="SW at least" value={swMin} onChange={setSwMin}
                                 step={0.05} min={0} max={1} />
            <span style={{ font: "400 9.5px/1.5 var(--font-sans)", color: "var(--ink-6)" }}>
              Porosity is read as a fraction and permeability in millidarcy. A
              porosity curve stored in percent types the reservoir as a hundred
              times better than it is.
            </span>
          </div>
        </Panel>

        <Panel title="Wells" meta={`${chosen.size} of ${wells.length}`}>
          <div style={{ display: "flex", flexDirection: "column", maxHeight: 180, overflowY: "auto" }}>
            {wells.map((well) => (
              <Checkbox
                key={well.id}
                checked={chosen.has(well.id)}
                label={well.name}
                dot={well.symbol_color || undefined}
                onChange={(on) => {
                  const next = new Set(chosen);
                  on ? next.add(well.id) : next.delete(well.id);
                  setSelection(next);
                }}
              />
            ))}
          </div>
        </Panel>

        <Panel title="Method">
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            <Dropdown
              label="Separation"
              value={method}
              options={methods.map((m) => ({ value: m.key, label: m.label, hint: m.note }))}
              onChange={setMethod}
            />
            {chosenMethod && (
              <span style={{ font: "400 9.5px/1.5 var(--font-sans)", color: "var(--ink-6)" }}>
                {chosenMethod.note}
              </span>
            )}
            {(clustering || method === "fzi") && (
              <NumberField label="Rock types" value={classes} onChange={setClasses}
                           step={1} min={2} max={12} />
            )}
            {clustering && (
              <Dropdown
                label="Clustered in"
                value={space}
                options={(catalogue.data?.features ?? []).map((f) => ({
                  value: f.key, label: f.label, hint: f.note,
                }))}
                onChange={setSpace}
              />
            )}
            {method === "manual" && (
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ font: "500 9.5px var(--font-mono)", color: "var(--ink-5)" }}>
                  FZI cutoffs
                </span>
                <input
                  value={cutoffs}
                  onChange={(event) => setCutoffs(event.target.value)}
                  aria-label="FZI cutoffs"
                  style={{
                    padding: "5px 7px",
                    background: "var(--panel-inset)",
                    border: "1px solid var(--border-control)",
                    borderRadius: "var(--radius)",
                    color: "var(--ink-2)",
                    font: "400 10.5px var(--font-mono)",
                  }}
                />
              </label>
            )}
          </div>
        </Panel>

        <Panel title="Machine learning">
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            <Checkbox
              checked={mlOn}
              onChange={setMlOn}
              label="Learn the rock types from logs"
            />
            <span style={{ font: "400 9.5px/1.5 var(--font-sans)", color: "var(--ink-6)" }}>
              A classifier trained on the rock types above, reading the log
              curves instead of φ and k. It earns its keep on the wells that
              have no permeability at all — and nowhere else.
            </span>
            {mlOn && (
              <>
                <Dropdown
                  label="Classifier"
                  value={mlKind}
                  options={(catalogue.data?.learners ?? []).map((m) => ({
                    value: m.key,
                    label: m.available ? m.label : `${m.label} — not installed`,
                    hint: m.note,
                    disabled: !m.available,
                  }))}
                  onChange={setMlKind}
                />
                <span style={{ font: "500 9.5px var(--font-mono)", color: "var(--ink-5)" }}>
                  Input curves
                </span>
                <div style={{ display: "flex", flexDirection: "column", maxHeight: 170, overflowY: "auto" }}>
                  {available
                    .filter((name) => name !== phi && name !== perm)
                    .map((name) => (
                      <Checkbox
                        key={name}
                        checked={inputs.includes(name)}
                        label={name}
                        onChange={(on) =>
                          setMlInputs(on ? [...inputs, name] : inputs.filter((c) => c !== name))
                        }
                      />
                    ))}
                </div>
              </>
            )}
          </div>
        </Panel>
      </div>

      <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, padding: 10, gap: 9 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, flex: "none" }}>
          <Button variant="primary" onClick={() => run(false)} busy={running}
                  disabled={!phi || !perm || chosen.size === 0}>
            Separate the rock types
          </Button>
          <Button
            variant="ghost"
            onClick={() => run(true)}
            disabled={running || !result || !mayPublish}
            title={mayPublish
              ? "Write ROCKTYPE, FZI, RQI and R35 into every well"
              : "Your role on this project may not publish curves"}
          >
            Publish as curves
          </Button>
          <span style={{ flex: 1 }} />
          {result && (
            <Segmented
              value={colourBy}
              options={[
                { value: "class", label: "Colour by rock type" },
                { value: "well", label: "Colour by well" },
              ]}
              onChange={(value) => setColourBy(value as "class" | "well")}
              size="sm"
            />
          )}
        </div>

        {!result && !running && (
          <Empty>
            Pick the porosity and permeability curves, choose how they should be
            separated, and run it. Nothing is computed until then.
          </Empty>
        )}
        {running && !result && <Loading label="Separating…" />}

        {result && (
          <>
            <PoroPermPlot payload={result} colourBy={colourBy} />
            <Segmented
              value={tab}
              options={[
                { value: "classes", label: "Rock types" },
                { value: "sweep", label: "Class count" },
                { value: "ml", label: "Machine learning" },
                { value: "wells", label: "Wells & samples" },
              ]}
              onChange={(value) => setTab(value as Tab)}
              size="sm"
            />
            <div className="scroll-y" style={{ flex: 1, minHeight: 120 }}>
              {tab === "classes" && <ClassTable payload={result} />}
              {tab === "sweep" && <SweepTable payload={result} asked={classes} />}
              {tab === "ml" && <MlReport payload={result} />}
              {tab === "wells" && <WellTable payload={result} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Gambar */

/**
 * Awan φ–log k, diwarnai menurut jenis batuan.
 *
 * Sumbu tegaknya logaritmik dan tidak bisa dipilih sebaliknya: permeabilitas
 * membentang beberapa dekade, dan sumbu linier memampatkan seluruh batuan
 * ketat menjadi satu garis di dasar kotak — yaitu justru bagian yang sedang
 * dipisahkan menjadi jenis-jenis.
 */
function PoroPermPlot({
  payload,
  colourBy,
}: {
  payload: RockTypingPayload;
  colourBy: "class" | "well";
}) {
  const { points } = payload;
  const wellColors = useMemo(
    () => distinctColors(payload.wells.map((w) => w.color)),
    [payload.wells],
  );

  const axes = useMemo(() => {
    const phis = points.phi.filter((v) => Number.isFinite(v));
    const perms = decadeBounds(points.perm);
    if (!phis.length || !perms) return null;
    const hi = Math.max(0.05, Math.ceil(Math.max(...phis) * 20) / 20);
    return { phi: [0, hi] as [number, number], perm: perms };
  }, [points]);

  const at = (phi: number, perm: number) => {
    if (!axes) return null;
    const tx = linearPlace(phi, axes.phi[0], axes.phi[1]);
    const ty = logPlace(perm, axes.perm[0], axes.perm[1]);
    if (!Number.isFinite(tx) || !Number.isFinite(ty)) return null;
    if (tx < 0 || tx > 1 || ty < 0 || ty > 1) return null;
    return { x: PAD + tx * SPAN, y: PAD + (1 - ty) * SPAN };
  };

  const path = (phi: number[], perm: number[]): string => {
    const parts: string[] = [];
    // Ruas yang keluar kotak diputus, bukan dijepit ke tepinya dan bukan pula
    // dilompati diam-diam: sebuah garis yang menyambung melintasi bagian yang
    // tidak tergambar berjalan lurus dari tepi ke tepi sebagai hubungan yang
    // tidak pernah dinyatakan siapa pun. Karena itu pena diangkat.
    let down = false;
    for (let i = 0; i < phi.length; i++) {
      const spot = at(phi[i], perm[i]);
      if (!spot) {
        down = false;
        continue;
      }
      parts.push(`${down ? "L" : "M"}${spot.x.toFixed(2)} ${spot.y.toFixed(2)}`);
      down = true;
    }
    return parts.join(" ");
  };

  if (!axes) {
    return (
      <Empty>No sample carries both a porosity and a positive permeability.</Empty>
    );
  }

  const permTicks = decadeTicks(axes.perm[0], axes.perm[1]);
  const phiTicks = [0, 0.25, 0.5, 0.75, 1].map((t) => axes.phi[0] + t * (axes.phi[1] - axes.phi[0]));
  const legend = occupied(payload.classes);

  return (
    <div style={{ display: "flex", gap: 6, flex: 1.8, minHeight: 220 }}>
      <div style={{ display: "flex", alignItems: "center", flex: "none" }}>
        <span
          style={{
            font: "500 10px var(--font-mono)",
            color: "var(--ink-4)",
            transform: "rotate(-90deg)",
            whiteSpace: "nowrap",
          }}
        >
          {payload.perm_label} mD · log
        </span>
      </div>
      <div style={{ width: 42, flex: "none", position: "relative" }}>
        {permTicks.map((tick) => (
          <span
            key={tick}
            style={{
              position: "absolute",
              right: 4,
              top: `${PAD + (1 - logPlace(tick, axes.perm[0], axes.perm[1])) * SPAN}%`,
              transform: "translateY(-50%)",
              font: "400 9px var(--font-mono)",
              color: "var(--ink-5)",
              whiteSpace: "nowrap",
            }}
          >
            {permText(tick)}
          </span>
        ))}
      </div>

      <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
        <div
          style={{
            position: "relative",
            flex: 1,
            minHeight: 160,
            background: "var(--canvas-deep)",
            border: "1px solid var(--line-strong)",
            borderRadius: "var(--radius-panel)",
            overflow: "hidden",
          }}
        >
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
            aria-label={`Rock typing ${payload.phi_label} against ${payload.perm_label}`}
          >
            {phiTicks.map((tick) => {
              const t = linearPlace(tick, axes.phi[0], axes.phi[1]);
              return (
                <line key={`gx${tick}`} x1={PAD + t * SPAN} x2={PAD + t * SPAN} y1={0} y2={100}
                      stroke="var(--grid-line-strong)" strokeWidth={1}
                      vectorEffect="non-scaling-stroke" />
              );
            })}
            {permTicks.map((tick) => {
              const t = logPlace(tick, axes.perm[0], axes.perm[1]);
              return (
                <line key={`gy${tick}`} x1={0} x2={100} y1={PAD + (1 - t) * SPAN}
                      y2={PAD + (1 - t) * SPAN} stroke="var(--grid-line-strong)"
                      strokeWidth={1} vectorEffect="non-scaling-stroke" />
              );
            })}

            {points.phi.map((value, index) => {
              const spot = at(value, points.perm[index]);
              if (!spot) return null;
              const colour = colourBy === "class"
                ? classColor(points.class[index])
                : wellColors[points.well[index]] ?? "var(--ink-5)";
              // Ruas selebar nol berujung bulat, dan bukan sebuah lingkaran.
              // Kotak ini memakai preserveAspectRatio="none" supaya awannya
              // mengisi berapa pun ruang yang ada, dan sebuah lingkaran di
              // dalamnya diregangkan bersama sumbunya: pada kotak selebar tiga
              // kali tingginya, tiap titik menjadi elips melintang yang
              // menyambung dengan tetangganya dan membuat awan renggang
              // terlihat rapat. Goresan yang tidak ikut diskalakan diukur dalam
              // piksel, jadi titiknya bundar pada bentuk kotak apa pun.
              return (
                <line
                  key={index}
                  x1={spot.x}
                  y1={spot.y}
                  x2={spot.x}
                  y2={spot.y}
                  stroke={colour}
                  strokeWidth={2.4}
                  strokeOpacity={0.85}
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              );
            })}

            {payload.boundaries.map((line: RockTypeBoundary) => (
              <path key={line.label} d={path(line.phi, line.perm)} fill="none"
                    stroke="var(--ink-4)" strokeWidth={1} strokeDasharray="4 3"
                    vectorEffect="non-scaling-stroke" />
            ))}

            {legend.map((row) => {
              const line = fitLine(row.fit);
              if (!line) return null;
              return (
                <path key={`fit${row.index}`} d={path(line.phi, line.perm)} fill="none"
                      stroke={classColor(row.index)} strokeWidth={1.4}
                      vectorEffect="non-scaling-stroke" />
              );
            })}
          </svg>

          {payload.boundaries.map((line) => {
            const last = at(line.phi[line.phi.length - 1], line.perm[line.perm.length - 1]);
            if (!last) return null;
            return (
              <span
                key={`tag${line.label}`}
                style={{
                  position: "absolute",
                  left: `${Math.min(88, last.x)}%`,
                  top: `${Math.max(2, Math.min(94, last.y))}%`,
                  transform: "translate(-100%, -120%)",
                  font: "500 9px var(--font-mono)",
                  color: "var(--ink-5)",
                  whiteSpace: "nowrap",
                }}
              >
                {line.label}
              </span>
            );
          })}

          <span
            style={{
              position: "absolute",
              left: 8,
              top: 6,
              font: "400 9px var(--font-mono)",
              color: "var(--ink-6)",
            }}
          >
            {points.shown.toLocaleString("en-US")} of {points.count.toLocaleString("en-US")} samples drawn
          </span>
        </div>

        <div style={{ position: "relative", height: 16, flex: "none" }}>
          {phiTicks.map((tick) => (
            <span
              key={tick}
              style={{
                position: "absolute",
                left: `${PAD + linearPlace(tick, axes.phi[0], axes.phi[1]) * SPAN}%`,
                transform: "translateX(-50%)",
                font: "400 9px var(--font-mono)",
                color: "var(--ink-5)",
              }}
            >
              {tick.toFixed(2)}
            </span>
          ))}
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", flex: "none" }}>
          <span style={{ font: "500 10px var(--font-mono)", color: "var(--ink-4)" }}>
            {payload.phi_label} v/v
          </span>
          {colourBy === "class"
            ? legend.map((row) => (
                <LegendDot key={row.index} color={classColor(row.index)}
                           label={`${row.name} · ${row.n.toLocaleString("en-US")}`} />
              ))
            : payload.wells.map((well, index) => (
                <LegendDot key={well.id} color={wellColors[index]}
                           label={`${well.name} · ${well.count.toLocaleString("en-US")}`} />
              ))}
        </div>
      </div>
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      <span style={{ width: 8, height: 8, borderRadius: 2, background: color, flex: "none" }} />
      <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-5)" }}>{label}</span>
    </span>
  );
}

/* ------------------------------------------------------------------ Tabel */

const cell: React.CSSProperties = {
  padding: "5px 9px",
  textAlign: "right",
  font: "400 10.5px var(--font-mono)",
  borderBottom: "1px solid var(--line-soft)",
  whiteSpace: "nowrap",
};
const head: React.CSSProperties = {
  ...cell,
  font: "600 9.5px var(--font-mono)",
  color: "var(--ink-5)",
  borderBottom: "1px solid var(--line-strong)",
  position: "sticky",
  top: 0,
  background: "var(--panel)",
};
const left: React.CSSProperties = { textAlign: "left" };

function Table({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ border: "1px solid var(--line)", borderRadius: 7, overflow: "hidden" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", background: "var(--panel-inset)" }}>
        {children}
      </table>
    </div>
  );
}

/**
 * Satu baris per jenis batuan, dan persamaannya sendiri di ujung kanan.
 *
 * Persamaan itulah alasan seluruh layar ini ada: k = 10^(a·φ + b) di dalam
 * satu jenis batuan adalah taksiran yang bisa dipakai, sedangkan persamaan
 * yang sama dipasang atas seluruh awan adalah taksiran yang meleset di
 * mana-mana dengan r² yang tetap terlihat lumayan.
 */
function ClassTable({ payload }: { payload: RockTypingPayload }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
      <Table>
        <thead>
          <tr>
            <th style={{ ...head, ...left }}>Rock type</th>
            <th style={head}>n</th>
            <th style={head}>Share</th>
            <th style={head}>φ p50</th>
            <th style={head}>k geo-mean</th>
            <th style={head}>FZI p50</th>
            <th style={head}>R35 p50 µm</th>
            <th style={{ ...head, ...left }}>φ–k relation</th>
            <th style={head}>r²</th>
          </tr>
        </thead>
        <tbody>
          {payload.classes.map((row) => (
            <tr key={row.index}>
              <td style={{ ...cell, ...left, color: "var(--ink-2)" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2,
                                 background: classColor(row.index), flex: "none" }} />
                  {row.name}
                </span>
              </td>
              <td style={cell}>{row.n.toLocaleString("en-US")}</td>
              <td style={cell}>{(row.share * 100).toFixed(1)}%</td>
              <td style={cell}>{num(row.phi?.p50, 3)}</td>
              <td style={cell}>{permText(row.perm_geometric_mean)}</td>
              <td style={cell}>{num(row.fzi?.p50, 2)}</td>
              <td style={cell}>{num(row.r35?.p50, 2)}</td>
              <td style={{ ...cell, ...left, color: "var(--ink-4)" }}>{equationOf(row.fit)}</td>
              <td style={cell}>{num(row.fit?.r2, 3)}</td>
            </tr>
          ))}
        </tbody>
      </Table>

      <span style={{ font: "400 10px/1.6 var(--font-sans)", color: "var(--ink-6)" }}>
        Permeability is averaged on the decade — one 1000 mD sample among nine of
        1 mD reports 2 mD here and 100 mD as an arithmetic mean, and only one of
        those describes the rock. The relation is fitted to log k, so it reads as
        k = 10^(a·φ + b) and holds inside this rock type only.
        {payload.method.detail?.ordering ? ` ${String(payload.method.detail.ordering)}` : ""}
      </span>
      {payload.published && <PublishedNote payload={payload} />}
    </div>
  );
}

function PublishedNote({ payload }: { payload: RockTypingPayload }) {
  const rows = payload.published?.wells ?? [];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ font: "600 10px var(--font-mono)", color: "var(--ink-4)" }}>
        Published as curve set {payload.published?.set}
      </span>
      <Table>
        <thead>
          <tr>
            <th style={{ ...head, ...left }}>Well</th>
            <th style={{ ...head, ...left }}>Curves</th>
            <th style={head}>Typed</th>
            <th style={head}>Predicted</th>
            <th style={head}>Version</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.well}>
              <td style={{ ...cell, ...left, color: "var(--ink-2)" }}>{row.well}</td>
              <td style={{ ...cell, ...left, color: "var(--ink-4)" }}>
                {row.curves?.join(", ") ?? row.reason ?? "—"}
              </td>
              <td style={cell}>{row.typed?.toLocaleString("en-US") ?? "—"}</td>
              <td style={cell}>{row.predicted?.toLocaleString("en-US") ?? "—"}</td>
              <td style={cell}>{row.version ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      <span style={{ font: "400 10px/1.6 var(--font-sans)", color: "var(--ink-6)" }}>
        {payload.published?.note}
      </span>
    </div>
  );
}

/**
 * Sapuan jumlah kelas — satu-satunya angka yang bisa membantah pilihan di kiri.
 *
 * Sebuah algoritma pengelompokan selalu memulangkan kelompok sebanyak yang
 * diminta, juga dari awan yang sebenarnya satu. Tabel ini berdiri di sini
 * supaya pertanyaan "apakah empat memang ada di datanya" punya tempat untuk
 * dijawab, dan bukan supaya angkanya menggantikan geologi.
 */
function SweepTable({ payload, asked }: { payload: RockTypingPayload; asked: number }) {
  if (!payload.sweep) {
    return (
      <Empty>
        This method sets its classes by cutoff, not by clustering, so there is no
        class count to sweep. The cutoffs themselves are reported with the method.
      </Empty>
    );
  }
  const verdict = sweepVerdict(payload.sweep.suggested, asked);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
      {verdict && <Notice tone="info">{verdict}</Notice>}
      <Table>
        <thead>
          <tr>
            <th style={{ ...head, ...left }}>Classes</th>
            <th style={head}>Silhouette</th>
            <th style={head}>Inertia</th>
            <th style={head}>BIC</th>
          </tr>
        </thead>
        <tbody>
          {payload.sweep.rows.map((row) => (
            <tr key={row.classes}>
              <td style={{ ...cell, ...left, color: "var(--ink-2)" }}>
                {row.classes}
                {row.classes === payload.sweep?.suggested ? " · cleanest" : ""}
              </td>
              <td style={cell}>{num(row.silhouette, 3)}</td>
              <td style={cell}>{num(row.inertia, 1)}</td>
              <td style={cell}>{num(row.bic, 0)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      <span style={{ font: "400 10px/1.6 var(--font-sans)", color: "var(--ink-6)" }}>
        {payload.sweep.note} Scored on {payload.sweep.scored_on.toLocaleString("en-US")} rows.
      </span>
    </div>
  );
}

/**
 * Laporan penggolong: angka buta lebih dulu, angka latih di sebelahnya.
 *
 * Keduanya berdampingan justru supaya selisihnya terlihat. Model yang hafal
 * memulangkan akurasi latih 0,99 dan akurasi buta 0,6, dan tidak ada satu pun
 * angka tunggal yang bisa mengatakan itu.
 */
function MlReport({ payload }: { payload: RockTypingPayload }) {
  const ml = payload.ml;
  if (!ml) {
    return (
      <Empty>
        No classifier was trained. Switch it on in the left rail, name the input
        curves, and run again — it is what lets a well with no permeability be
        typed at all.
      </Empty>
    );
  }
  const report = ml.report;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
        <KeyValue k="Classifier" v={ml.label} />
        <KeyValue k="Reads" v={ml.inputs.join(", ")} />
        <KeyValue k="Blind accuracy" v={num(report.blind_metrics.accuracy, 3)} />
        <KeyValue k="Blind macro F1" v={num(report.blind_metrics.f1_macro, 3)} />
        <KeyValue k="Trained accuracy" v={num(report.trained_metrics.accuracy, 3)} />
        <KeyValue k="Held back by" v={report.split} />
      </div>

      <Notice tone="info">{report.note}</Notice>

      <Table>
        <thead>
          <tr>
            <th style={{ ...head, ...left }}>Rock type</th>
            <th style={head}>Blind samples</th>
            <th style={head}>Recall</th>
            <th style={head}>Precision</th>
          </tr>
        </thead>
        <tbody>
          {report.per_class.map((row) => (
            <tr key={row.index}>
              <td style={{ ...cell, ...left, color: "var(--ink-2)" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2,
                                 background: classColor(row.index), flex: "none" }} />
                  {row.name}
                </span>
              </td>
              <td style={cell}>{row.support.toLocaleString("en-US")}</td>
              <td style={cell}>{num(row.recall, 3)}</td>
              <td style={cell}>{num(row.precision, 3)}</td>
            </tr>
          ))}
        </tbody>
      </Table>

      <span style={{ font: "600 10px var(--font-mono)", color: "var(--ink-4)" }}>
        Where the misses went — rows are the truth, columns the guess
      </span>
      <Table>
        <thead>
          <tr>
            <th style={{ ...head, ...left }}>Truth \ guess</th>
            {ml.class_names.map((name) => (
              <th key={name} style={head}>{name}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {report.confusion.map((row, index) => (
            <tr key={index}>
              <td style={{ ...cell, ...left, color: "var(--ink-2)" }}>
                {ml.class_names[index] ?? index}
              </td>
              {row.map((value, column) => (
                <td
                  key={column}
                  style={{
                    ...cell,
                    color: index === column ? "var(--ok-ink)"
                      : value > 0 ? "var(--warn-ink)" : "var(--ink-7)",
                  }}
                >
                  {value.toLocaleString("en-US")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>

      {report.importances && (
        <>
          <span style={{ font: "600 10px var(--font-mono)", color: "var(--ink-4)" }}>
            Which curve did the deciding
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {report.importances.map((row) => (
              <div key={row.curve} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 62, font: "400 10px var(--font-mono)", color: "var(--ink-4)" }}>
                  {row.curve}
                </span>
                <span style={{ flex: 1, height: 7, background: "var(--panel-inset)", borderRadius: 3 }}>
                  <span
                    style={{
                      display: "block",
                      width: `${Math.max(1, row.weight * 100)}%`,
                      height: "100%",
                      background: "var(--accent)",
                      borderRadius: 3,
                    }}
                  />
                </span>
                <span style={{ width: 42, textAlign: "right", font: "400 10px var(--font-mono)", color: "var(--ink-5)" }}>
                  {(row.weight * 100).toFixed(1)}%
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Sumur yang ikut, dan sumur yang dilewati berikut sebabnya. */
function WellTable({ payload }: { payload: RockTypingPayload }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
      <Table>
        <thead>
          <tr>
            <th style={{ ...head, ...left }}>Well</th>
            <th style={head}>Samples</th>
            <th style={{ ...head, ...left }}>MD range</th>
          </tr>
        </thead>
        <tbody>
          {payload.wells.map((well) => (
            <tr key={well.id}>
              <td style={{ ...cell, ...left, color: "var(--ink-2)" }}>{well.name}</td>
              <td style={cell}>{well.count.toLocaleString("en-US")}</td>
              <td style={{ ...cell, ...left, color: "var(--ink-5)" }}>
                {well.md_range ? `${well.md_range[0]?.toFixed(1)}–${well.md_range[1]?.toFixed(1)}` : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
        <KeyValue k="Interval" v={payload.interval || "every depth"} />
        <KeyValue k="Before the filter" v={payload.filter.before.toLocaleString("en-US")} />
        <KeyValue k="After it" v={payload.filter.after.toLocaleString("en-US")} />
        <KeyValue k="φ p50" v={num(payload.stats.phi?.p50, 3)} />
        <KeyValue k="k p50" v={permText(payload.stats.perm?.p50)} />
      </div>

      {payload.skipped.length > 0 && (
        <Notice tone="warn">
          Left out, and why: {payload.skipped.map((row) => `${row.well} — ${row.reason}`).join("; ")}.
        </Notice>
      )}
    </div>
  );
}
